import { API_URL } from "./api";
import { supabase } from "@/integrations/supabase/client";

export interface ITutorial {
  documentId: string;
  is_completed?: boolean;
  is_uninterested?: boolean;
}

export interface IPlaylist {
  title: string;
  documentId: string;
  is_trial: boolean;
  slug: string | null;
  tutorials: ITutorial[];
}

export interface ICategory {
  title: string;
  documentId: string;
  playlists: IPlaylist[];
  is_trial?: boolean;
}

export interface ICategoriesResponse {
  data: {
    categories: ICategory[];
  };
}

export async function fetchCategories(
  userId: string
): Promise<ICategory[] | null> {
  try {
    const query = `
			query Categories {
				categories(pagination: { limit: 100 }) {
					title
					documentId
					is_trial
					playlists(pagination: { limit: 100 }) {
						title
						documentId
						is_trial
						slug
						tutorials(pagination: { limit: 100 }) {
							documentId
						}
					}
				}
			}
		`;

    const response = await fetch(`${API_URL}/graphql`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) throw new Error("Failed to fetch categories");

    const { data } = await response.json();

    const categoriesOrder = [
      "Основы ИИ",
      "Промпт-инжиниринг",
      "Продуктивность",
      "Создание контента",
      "Автоматизация",
      "Готовые промпты",
    ];

    const orderMap = new Map(
      categoriesOrder.map((title, index) => [title, index])
    );

    const categories: ICategory[] = [...data.categories].sort((a, b) => {
      const indexA = orderMap.has(a.title) ? orderMap.get(a.title)! : Infinity;
      const indexB = orderMap.has(b.title) ? orderMap.get(b.title)! : Infinity;
      return indexA - indexB;
    });

    if (categories.length === 0) return [];
    const allTutorialIds = categories
      .flatMap((c) => c.playlists)
      .flatMap((p) => p.tutorials)
      .map((t) => t.documentId);

    if (allTutorialIds.length === 0) return categories;

    const { data: completedData, error } = await supabase
      .from("tutorial_preferences")
      .select("document_id, preference_type")
      .eq("user_id", userId)
      .in("document_id", allTutorialIds)
      .in("preference_type", ["completed", "uninterested"]);

    if (error) throw error;

    const completedSet = new Set(
      completedData
        .filter((d) => d.preference_type === "completed")
        .map((d) => d.document_id)
    );

    const uninterestedSet = new Set(
      completedData
        .filter((d) => d.preference_type === "uninterested")
        .map((d) => d.document_id)
    );
    const categoriesWithProgress = categories.map((category) => ({
      ...category,
      playlists: category.playlists.map((playlist) => {
        const tutorialsWithStatus = playlist.tutorials.map((tutorial) => ({
          ...tutorial,
          is_completed: completedSet.has(tutorial.documentId),
          is_uninterested: uninterestedSet.has(tutorial.documentId),
        }));

        return {
          ...playlist,
          tutorials: tutorialsWithStatus,
        };
      }),
    }));
    return categoriesWithProgress;
  } catch (error: any) {
    console.error("Error in fetchCategoriesWithProgressAndMarking:", error);
    return null;
  }
}
